import { useState, useRef, useCallback, useEffect } from "react";

import {
  getInitialArtefactHeight,
  saveArtefactHeight,
  constrainArtefactHeight,
  DEFAULT_ARTEFACT_HEIGHT,
  MAX_ARTEFACT_HEIGHT_RATIO,
} from "@/utils/artefactHeight";

interface UseResizableArtefactCardReturn {
  /** Current shared expanded artefact height in pixels */
  artefactHeight: number;
  /** Whether the user is currently resizing an artefact card */
  isResizingArtefact: boolean;
  /** Handler for starting the resize operation */
  handleArtefactResizeMouseDown: (e: React.MouseEvent) => void;
  /** Handler for keyboard-driven resize (delta in pixels; positive grows) */
  handleArtefactResizeKeyboard: (delta: number) => void;
  /** Reset the height to the default */
  resetArtefactHeight: () => void;
  /** Maximum allowed artefact height in pixels (reactive, updates on window resize) */
  maxArtefactHeight: number;
}

/**
 * Hook for managing a vertically resizable expanded artefact card with
 * localStorage persistence. The height is shared across every expanded card
 * in the artefacts panel — dragging from any handle resizes them all.
 *
 * Drag direction: dragging DOWN grows the card; dragging UP shrinks it.
 * Mirrors the structure of useResizablePromptArea but inverts the drag
 * direction (since the handle sits at the bottom of each card) and starts
 * with a known default rather than null/auto sizing.
 */
export function useResizableArtefactCard(): UseResizableArtefactCardReturn {
  const [artefactHeight, setArtefactHeight] = useState<number>(getInitialArtefactHeight);
  const [isResizingArtefact, setIsResizingArtefact] = useState(false);
  const [maxArtefactHeight, setMaxArtefactHeight] = useState(
    () => window.innerHeight * MAX_ARTEFACT_HEIGHT_RATIO,
  );
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const artefactHeightRef = useRef(artefactHeight);

  artefactHeightRef.current = artefactHeight;

  const handleArtefactResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();

    // Capture starting position and current shared height
    startYRef.current = e.clientY;
    startHeightRef.current = artefactHeightRef.current;

    // Prevent text selection and override cursor during drag
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";

    setIsResizingArtefact(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizingArtefact) return;

      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Throttle using requestAnimationFrame
      animationFrameRef.current = requestAnimationFrame(() => {
        // Dragging DOWN (positive clientY delta) grows the card
        const delta = e.clientY - startYRef.current;
        const newHeight = startHeightRef.current + delta;
        const constrained = constrainArtefactHeight(newHeight, window.innerHeight);

        setArtefactHeight((prev) => {
          const diff = Math.abs(prev - constrained);

          return diff > 1 ? constrained : prev;
        });
      });
    },
    [isResizingArtefact],
  );

  const handleMouseUp = useCallback(() => {
    // Clean up animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Restore body cursor and text selection
    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    setIsResizingArtefact(false);

    // Save the current height to localStorage
    setArtefactHeight((current) => {
      saveArtefactHeight(current);

      return current;
    });
  }, []);

  const handleArtefactResizeKeyboard = useCallback((delta: number) => {
    setArtefactHeight((prev) => {
      const newHeight = constrainArtefactHeight(prev + delta, window.innerHeight);

      saveArtefactHeight(newHeight);

      return newHeight;
    });
  }, []);

  const resetArtefactHeight = useCallback(() => {
    setArtefactHeight(DEFAULT_ARTEFACT_HEIGHT);
    saveArtefactHeight(DEFAULT_ARTEFACT_HEIGHT);
  }, []);

  // Add/remove global mouse event listeners during resize
  useEffect(() => {
    if (isResizingArtefact) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizingArtefact, handleMouseMove, handleMouseUp]);

  // Re-clamp height on window resize and keep maxArtefactHeight in sync
  useEffect(() => {
    const updateMax = () => {
      const maxH = window.innerHeight * MAX_ARTEFACT_HEIGHT_RATIO;

      setMaxArtefactHeight(maxH);

      const current = artefactHeightRef.current;

      if (current > maxH) {
        const constrained = constrainArtefactHeight(current, window.innerHeight);

        setArtefactHeight(constrained);
        saveArtefactHeight(constrained);
      }
    };

    updateMax();

    window.addEventListener("resize", updateMax);

    return () => window.removeEventListener("resize", updateMax);
  }, []);

  return {
    artefactHeight,
    isResizingArtefact,
    handleArtefactResizeMouseDown,
    handleArtefactResizeKeyboard,
    resetArtefactHeight,
    maxArtefactHeight,
  };
}
